import { useState, useEffect, useCallback } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export function usePWA() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstallable, setIsInstallable] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  useEffect(() => {
    // Check if already installed
    const checkInstalled = () => {
      const isStandalone = window.matchMedia("(display-mode: standalone)").matches;
      const isIOSStandalone = (navigator as any).standalone === true;
      setIsInstalled(isStandalone || isIOSStandalone);
    };
    
    checkInstalled();

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setIsInstallable(true);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setIsInstallable(false);
      setDeferredPrompt(null);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!deferredPrompt) return false;

    try {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      
      if (outcome === "accepted") {
        setIsInstalled(true);
        setIsInstallable(false);
      }
      
      setDeferredPrompt(null);
      return outcome === "accepted";
    } catch (error) {
      console.error("Error prompting install:", error);
      return false;
    }
  };

  /**
   * Force update the PWA by:
   * 1. Updating all service worker registrations
   * 2. Clearing all caches
   * 3. Reloading the page with cache bust
   */
  const forceUpdate = useCallback(async () => {
    setIsUpdating(true);
    
    // Mark that we're updating - this will be checked after reload
    localStorage.setItem("pwa_updating", Date.now().toString());
    
    try {
      // 1. Update service workers
      if ("serviceWorker" in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          await registration.update();
          // If there's a waiting worker, skip waiting and activate it
          if (registration.waiting) {
            registration.waiting.postMessage({ type: "SKIP_WAITING" });
          }
        }
      }

      // 2. Clear all caches
      if ("caches" in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map((cacheName) => caches.delete(cacheName))
        );
        console.log("[PWA] Cleared caches:", cacheNames);
      }

      // 3. Clear localStorage cache entries (PDV cache)
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (key.startsWith("pdv_cache_") || key.startsWith("offline_"))) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
      console.log("[PWA] Cleared localStorage cache keys:", keysToRemove);

      // 4. Small delay to ensure updates are processed
      await new Promise((resolve) => setTimeout(resolve, 500));

      // 5. Hard reload with cache bust parameter
      const url = new URL(window.location.href);
      url.searchParams.set("_cb", Date.now().toString());
      window.location.href = url.toString();
    } catch (error) {
      console.error("[PWA] Error during force update:", error);
      // Reload anyway as a fallback
      window.location.href = window.location.href + "?_cb=" + Date.now();
    }
  }, []);

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  return {
    isInstallable,
    isInstalled,
    isIOS,
    isUpdating,
    promptInstall,
    forceUpdate,
  };
}
